import type { CSSProperties } from "react";
import { ArtistIcon } from "../icons/ArtistIcon";

/** FNV-1a 32-bit hash — deterministic playlist color selection. */
function fnv1aHash(input: string): number {
	let hash = 2166136261;
	for (let i = 0; i < input.length; i += 1) {
		hash ^= input.charCodeAt(i);
		hash = Math.imul(hash, 16777619);
	}
	return hash >>> 0;
}

const PLACEHOLDER_HUES = [198, 268, 12, 142, 32, 322, 222];

export interface PlaylistPlaceholderProps {
	/** Seed for the color variant (typically the playlist id/title). */
	readonly seed: string;
	readonly size?: number;
	readonly className?: string;
}

/**
 * PlaylistPlaceholder — colored square placeholder with a music glyph, used
 * when a playlist has no artwork. Mirrors `DSPlaylistPlaceholder` on iOS with
 * 7 hue variants selected deterministically via FNV-1a hash of the seed.
 */
export function PlaylistPlaceholder({
	seed,
	size = 56,
	className = "",
}: Readonly<PlaylistPlaceholderProps>) {
	const variant = fnv1aHash(seed) % PLACEHOLDER_HUES.length;
	const hue = PLACEHOLDER_HUES[variant] ?? 198;
	const style: CSSProperties = {
		width: `${size}px`,
		height: `${size}px`,
		borderRadius: "var(--ds-radius-md)",
		display: "inline-grid",
		placeItems: "center",
		color: "rgb(255 255 255 / 0.92)",
		background: `linear-gradient(135deg, hsl(${hue} 48% 52%), hsl(${(hue + 28) % 360} 52% 42%))`,
	};
	return (
		<span
			className={`ds-playlist-placeholder ${className}`.trim()}
			style={style}
			data-variant={variant}
		>
			<ArtistIcon name="music" size={Math.round(size * 0.45)} />
		</span>
	);
}
